import React, { useEffect, useState, FormEvent } from "react";
import { Grid, Form, Button, Alert } from "tabler-react";
import { ContentTextProps, ShorthcutClicksProps } from "../../types";
import moment from "moment";
import TableCard from "../../components/tableCard";
import ServerSidePagination from "../../components/pagination/serverSidePagination";
import {
  SHORTHCUT_CLICKS_CHART,
  SHORTHCUT_CLICKS,
} from "../../graphql/queries";
import Chart from "../../components/chart";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import DatePicker from "react-datepicker";
import { products } from "../../constants";
import { getCountArr, isProduction } from "../../helpers";
import "./index.css";

const ShorthcutClicks = (props: ShorthcutClicksProps): JSX.Element => {
  const [shorthcutClicks, setShorthcutClicks] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [scanLogsPagination, setScanLogsPagination] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [total, setTotal] = useState(0);
  const [selectProduct, setSelectProduct] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const showButtons = localStorage.getItem("_rep");

  const [getCSV, { data, loading }] = useLazyQuery(SHORTHCUT_CLICKS);
  const [uploadToDrive, { data: driveData, loading: driveDataLoading }] =
    useLazyQuery(SHORTHCUT_CLICKS);

  const chartDataQuery = useQuery(SHORTHCUT_CLICKS_CHART, {
    variables: {
      hideHex: props.filterHexScans,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const tableData = {
    tData:
      shorthcutClicks &&
      shorthcutClicks.map((item: ContentTextProps) => {
        return {
          "User Email": item.email || "No email",
          "User Name": item.firstName + " " + item.lastName,
          "Click date": moment(new Date(+item.created_at))
            .utc()
            .format("DD-MM-YYYY hh:mm:ss a"),
          "Product Name": item.type,
        };
      }),
  };

  useEffect(() => {
    if (
      props.data &&
      props.data.ShortcutLog &&
      props.data.ShortcutLog.getShortcutLogs &&
      props.data.ShortcutLog.getShortcutLogs.shortcutLogs
    ) {
      setDataLoading(props.loading);
      setShorthcutClicks(props.data.ShortcutLog.getShortcutLogs.shortcutLogs);
      setScanLogsPagination(
        props.data.ShortcutLog.getShortcutLogs.shortcutLogs
      );
      setTotal(props.data.ShortcutLog.getShortcutLogs.count);
    }
  }, [props.data]);

  useEffect(() => {
    if (
      driveData &&
      driveData.ShortcutLog &&
      driveData.ShortcutLog.getShortcutLogs &&
      driveData.ShortcutLog.getShortcutLogs.csvUrl
    ) {
      window.open(
        isProduction()
          ? `https://api.hexometer.com/v2/app/social-callback/googleDrive?admin=admin&report=shortcutClick&url=${driveData.ShortcutLog.getShortcutLogs.csvUrl}`
          : `http://localhost:4000/api/app/social-callback/googleDrive?admin=admin&report=shortcutClick&url=${driveData.ShortcutLog.getShortcutLogs.csvUrl}`,
        "_blank"
      );
    }
  }, [driveData]);

  const handleInputChange = (e: FormEvent) =>
    setFilterInput((e.target as HTMLInputElement).value);

  const handleSelectedProduct = (e: FormEvent) =>
    setSelectProduct((e.target as HTMLInputElement).value);

  const handleFromDate = (date: Date) => {
    if (date) {
      return setFromDate(moment(date).format("YYYY-MM-DDT00:00:00.000"));
    }
    return setFromDate(null);
  };

  const handleToDate = (date: Date) => {
    if (date) {
      return setToDate(moment(date).format("YYYY-MM-DDT23:59:59.999"));
    }
    return setToDate(null);
  };

  const handleSubmit = (
    event: FormEvent,
    data: string,
    productName: string,
    startDate: string,
    endDate: string
  ) => {
    event.preventDefault();
    props.onFilterDataChange(data.trim());
    props.onFromDate(startDate);
    props.onToDate(endDate);

    if (productName !== "Select product") {
      props.onProductSelect(productName);
    } else {
      props.onProductSelect("");
    }
  };

  const handleSortingClick = (column: string) => {
    if (column === "Click date") props.setOrderBy("created_at");
    else if (column === "Product Name") props.setOrderBy("type");
    if (column === "Click date" || column === "Product Name")
      props.setOrderByASC(!props.orderByASC);
  };

  return (
    <Grid>
      <Grid.Row className="mb-5">
        <div className="col-12 col-md-8">
          {!chartDataQuery.loading &&
            chartDataQuery.data &&
            chartDataQuery.data.ShortcutLog &&
            chartDataQuery.data.ShortcutLog.getShortcutLogTimeSeries && (
              <Chart
                yColName="Shorthcut Clicks"
                regData={
                  chartDataQuery.data.ShortcutLog.getShortcutLogTimeSeries
                }
                interval={getCountArr(
                  chartDataQuery.data.ShortcutLog.getShortcutLogTimeSeries
                    .length
                )}
                chartName="Shorthcut Clicks Activity"
              />
            )}
        </div>
        <div className="col-12 col-md-4">
          <form
            onSubmit={(e) =>
              handleSubmit(e, filterInput, selectProduct, fromDate, toDate)
            }
          >
            <Form.Group>
              <Form.Input
                label="Input"
                className="margin-bottom"
                icon="search"
                position="append"
                placeholder="Search for User Email"
                value={filterInput}
                onChange={handleInputChange}
              />
              <Form.Select
                label="Product Name"
                onChange={handleSelectedProduct}
              >
                <option value="Select product">Select product</option>
                {products.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </Form.Select>
              <Form.Group
                label="From Date"
                className="shortcutClicksDatePicker"
              >
                <DatePicker
                  dateFormat="yyyy-MM-dd"
                  selected={fromDate ? new Date(fromDate) : null}
                  onChange={handleFromDate}
                  showMonthDropdown
                  placeholderText="Select a date"
                />
              </Form.Group>
              <Form.Group label="To Date" className="shortcutClicksDatePicker">
                <DatePicker
                  dateFormat="yyyy-MM-dd"
                  selected={toDate ? new Date(toDate) : null}
                  onChange={handleToDate}
                  showMonthDropdown
                  placeholderText="Select a date"
                  minDate={fromDate && new Date(fromDate)}
                />
              </Form.Group>
              <Button type="submit" icon="filter" color="primary">
                Filter
              </Button>
            </Form.Group>
          </form>
        </div>
      </Grid.Row>
      <Grid.Row className={showButtons === 'true' ? "justify-content-between" : "justify-content-end"}>
        {showButtons === 'true' && (
          <div className="col-12 col-md-3 col-xs-12">
            <Button.List>
              <Button
                onClick={() =>
                  getCSV({
                    variables: {
                      settings: {
                        ...props.variables,
                        csvDownload: true,
                      },
                    },
                  })
                }
                loading={loading}
                color="primary"
                mr={1}
              >
                Get CSV Link
              </Button>
              <Button
                onClick={() =>
                  uploadToDrive({
                    variables: {
                      settings: {
                        ...props.variables,
                        csvDownload: true,
                      },
                    },
                  })
                }
                loading={driveDataLoading}
                color="primary"
              >
                Save to Drive
              </Button>
            </Button.List>
            {!loading &&
              data &&
              data.ShortcutLog &&
              data.ShortcutLog.getShortcutLogs &&
              data.ShortcutLog.getShortcutLogs.csvUrl && (
                <Button
                  RootComponent="a"
                  href={data.ShortcutLog.getShortcutLogs.csvUrl}
                  icon="download"
                  className="mt-2"
                >
                  Download CSV
                </Button>
              )}
            <br />
          </div>
        )}
        <Grid.Col md={3}>
          <Alert type="info">Result Count - {total}</Alert>
        </Grid.Col>
      </Grid.Row>
      <Grid.Row cards deck>
        <ServerSidePagination
          data={scanLogsPagination}
          total={total}
          onPageNum={props.onPageNumberChange}
        >
          <TableCard
            loading={dataLoading}
            data={shorthcutClicks}
            tableData={tableData}
            onHeaderClick={handleSortingClick}
          />
        </ServerSidePagination>
      </Grid.Row>
      <Grid.Row className="justify-content-end">
        {total ? (
          <Grid.Col md={3}>
            <Alert type="info">Result Count - {total}</Alert>
          </Grid.Col>
        ) : null}
      </Grid.Row>
    </Grid>
  );
};

export default ShorthcutClicks;
